import React, { useState, useEffect } from "react";
import { Dropdown, message } from "antd";
import {
  Menu,
  X,
  User,
  LogOut,
  FileText,
  Bell,
  ChevronDown,
  Home,
  Info,
  MapPin,
  Calendar,
  Phone,
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import logo from "../../../assets/logo.png";

const HeaderComponent = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userType, setUserType] = useState(null); // 'authenticated' or null
  const [user, setUser] = useState(null);

  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();

  useEffect(() => {
    checkUserStatus();
  }, []);

  useEffect(() => {
    const handleUserUpdate = () => checkUserStatus();
    window.addEventListener("userLoggedIn", handleUserUpdate);
    return () => window.removeEventListener("userLoggedIn", handleUserUpdate);
  }, []);

  const checkUserStatus = () => {
    const authToken = localStorage.getItem("authToken");
    const userData = localStorage.getItem("user");
    if (authToken && userData) {
      setUserType("authenticated");
      setUser(JSON.parse(userData));
    } else {
      setUserType(null);
      setUser(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    messageApi.open({
      type: "success",
      content: "🙏 Logged out successfully. Thank you for visiting MahalaxmiPuja!",
      duration: 2,
    });
    setUserType(null);
    setUser(null);
    navigate("/");
  };

  const userMenuItems = [
    {
      key: "1",
      label: (
        <div className="flex items-center space-x-2 py-2">
          <User className="h-4 w-4" />
          <span>My Profile</span>
        </div>
      ),
      onClick: () => navigate("/profile"),
    },
    {
      key: "2",
      label: (
        <div className="flex items-center space-x-2 py-2">
          <FileText className="h-4 w-4" />
          <span>My Orders</span>
        </div>
      ),
      onClick: () => navigate("/orders"),
    },
    {
      key: "3",
      label: (
        <div className="flex items-center space-x-2 py-2">
          <Bell className="h-4 w-4" />
          <span>Notifications</span>
        </div>
      ),
      onClick: () => navigate("/notifications"),
    },
    { type: "divider" },
    {
      key: "5",
      label: (
        <div className="flex items-center space-x-2 py-2 text-red-600">
          <LogOut className="h-4 w-4" />
          <span>Logout</span>
        </div>
      ),
      onClick: handleLogout,
    },
  ];

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <header className="bg-white shadow-lg border-b-2 border-orange-200 sticky top-0 z-40">
      {contextHolder}
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center text-white text-lg">
              <img
                src={logo}
                alt="MahalaxmiPuja Logo"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">MahalaxmiPuja</h1>
              <p className="text-xs text-orange-600 font-medium">Kolhapur</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link to="/" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              Home
            </Link>
            <Link to="/services" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              Puja Booking
            </Link>
            <Link to="/temple" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              Temple Information
            </Link>
            <Link to="/about-us" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              About Us
            </Link>
            <Link to="/contact" className="text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200">
              Contact
            </Link>
          </nav>

          {/* Right Section - Authentication */}
          <div className="flex items-center space-x-4">
            {userType ? (
              <div className="hidden md:flex items-center">
                <Dropdown
                  menu={{ items: userMenuItems }}
                  placement="bottomRight"
                  trigger={["click"]}
                >
                  <button className="flex items-center space-x-2 p-2 text-gray-700 hover:text-orange-600 transition-all duration-300 rounded-lg hover:bg-orange-50">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold bg-gradient-to-r from-orange-500 to-red-500">
                      {user?.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div className="hidden lg:block text-left">
                      <div className="font-medium text-sm">
                        {user?.name || "User"}
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 hidden lg:block" />
                  </button>
                </Dropdown>
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                className="hidden md:flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-white bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transition-all duration-300"
              >
                <User className="h-4 w-4" />
                <span>Login</span>
              </button>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-2 text-gray-600 hover:text-orange-600 transition-colors"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <div className="py-4">
              <div className="mb-6">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 mb-3">
                  Navigation
                </div>
                <nav className="space-y-1">
                  {[
                    { to: "/", icon: <Home className="h-5 w-5" />, label: "Home" },
                    { to: "/services", icon: <Calendar className="h-5 w-5" />, label: "Puja Booking" },
                    { to: "/temple", icon: <MapPin className="h-5 w-5" />, label: "Temple Information" },
                    { to: "/about-us", icon: <Info className="h-5 w-5" />, label: "About Us" },
                    { to: "/contact", icon: <Phone className="h-5 w-5" />, label: "Contact" },
                  ].map(({ to, icon, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {icon}
                      <span className="font-medium">{label}</span>
                    </Link>
                  ))}
                </nav>
              </div>

              {userType ? (
                <div>
                  <div className="mx-4 mb-4 p-4 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg bg-gradient-to-r from-orange-500 to-red-500">
                        {user?.name?.charAt(0)?.toUpperCase() || "U"}
                      </div>
                      <div className="font-semibold text-gray-800">{user?.name || "User"}</div>
                    </div>
                  </div>

                  <div className="mb-4 space-y-1">
                    <button
                      onClick={() => { navigate("/profile"); setIsMenuOpen(false); }}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-200"
                    >
                      <User className="h-5 w-5" />
                      <span className="font-medium">My Profile</span>
                    </button>
                    <button
                      onClick={() => { navigate("/orders"); setIsMenuOpen(false); }}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-200"
                    >
                      <FileText className="h-5 w-5" />
                      <span className="font-medium">My Orders</span>
                    </button>
                    <button
                      onClick={() => { navigate("/notifications"); setIsMenuOpen(false); }}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-all duration-200"
                    >
                      <Bell className="h-5 w-5" />
                      <span className="font-medium">Notifications</span>
                    </button>
                  </div>

                  <div className="border-t border-gray-200 pt-4">
                    <button
                      onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                      className="w-full flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-all duration-200 font-medium"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="border-t border-gray-200 pt-4 px-4">
                  <button
                    onClick={() => { navigate("/login"); setIsMenuOpen(false); }}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-lg font-medium text-white bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 transition-all duration-300"
                  >
                    <User className="h-5 w-5" />
                    <span>Login</span>
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    Login with your mobile number to view your bookings
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default HeaderComponent;
